// FORFEO INC. - Lot 4R-C
// Contrat pur du moteur de réservation Forfeo Reserve.
// Reserve reste l'autorité des créneaux et réservations; Giftshop reste financier.

import {
  FORFEO_RESERVE_AUTHORITIES,
  hasForfeoReserveForbiddenClientFields,
  isForfeoReserveHalfOpenInterval,
  normalizeForfeoReserveIdempotencyKey,
  type ForfeoReserveInstantWindow,
} from './forfeo-reserve-availability.ts';

export const FORFEO_RESERVE_BOOKING_CONTRACT_VERSION =
  'forfeo-reserve-booking-4r-c-v1' as const;

export const FORFEO_RESERVE_HOLD_STATES = [
  'active',
  'consumed',
  'released',
  'expired',
] as const;

export type ForfeoReserveHoldState =
  (typeof FORFEO_RESERVE_HOLD_STATES)[number];

export const FORFEO_RESERVE_BOOKING_STATES = [
  'pending_confirmation',
  'pending_payment',
  'confirmed',
  'completed',
  'cancelled',
  'expired',
  'no_show',
  'manual_review',
] as const;

export type ForfeoReserveBookingState =
  (typeof FORFEO_RESERVE_BOOKING_STATES)[number];

export const FORFEO_RESERVE_WAITLIST_STATES = [
  'waiting',
  'offered',
  'converted',
  'cancelled',
  'expired',
] as const;

export type ForfeoReserveWaitlistState =
  (typeof FORFEO_RESERVE_WAITLIST_STATES)[number];

export const FORFEO_RESERVE_BOOKING_COMMANDS = [
  'create_hold',
  'release_hold',
  'confirm_free',
  'cancel_booking',
  'reschedule_booking',
  'mark_no_show',
  'join_waitlist',
  'expire_holds',
] as const;

export type ForfeoReserveBookingCommandKind =
  (typeof FORFEO_RESERVE_BOOKING_COMMANDS)[number];

export interface ForfeoReserveAvailabilitySearch
  extends ForfeoReserveInstantWindow {
  businessId: string;
  catalogItemId: string;
  partySize: number;
  timezone: string;
  correlationId: string;
}

export interface ForfeoReserveAvailabilitySearchResult
  extends ForfeoReserveInstantWindow {
  authority: 'RESERVE';
  businessId: string;
  catalogItemId: string;
  state: 'available' | 'limited' | 'fully_booked' | 'blocked' | 'closed';
  requestedCapacity: number;
  totalCapacity: number;
  committedCapacity: number;
  remainingCapacity: number;
  evaluatedAt: string;
  correlationId: string;
}

export interface ForfeoReserveHoldContract
  extends ForfeoReserveInstantWindow {
  id: string;
  businessId: string;
  catalogItemId: string;
  partySize: number;
  status: ForfeoReserveHoldState;
  expiresAt: string;
  revision: number;
  correlationId: string;
}

export interface ForfeoReserveBookingContract
  extends ForfeoReserveInstantWindow {
  id: string;
  businessId: string;
  catalogItemId: string;
  holdId?: string;
  partySize: number;
  state: ForfeoReserveBookingState;
  revision: number;
  correlationId: string;
}

export interface ForfeoReserveWaitlistContract
  extends ForfeoReserveInstantWindow {
  id: string;
  businessId: string;
  catalogItemId: string;
  partySize: number;
  status: ForfeoReserveWaitlistState;
  position: number;
  correlationId: string;
}

export interface ForfeoReserveBookingSnapshot {
  authority: 'RESERVE';
  businessId: string;
  startsAt: string;
  endsAt: string;
  catalogItems: readonly {
    id: string;
    label: string;
    kind: string;
    timezone: string;
    minimumPartySize: number;
    maximumPartySize: number;
    active: boolean;
  }[];
  holds: readonly ForfeoReserveHoldContract[];
  bookings: readonly ForfeoReserveBookingContract[];
  waitlist: readonly ForfeoReserveWaitlistContract[];
  generatedAt: string;
}

export interface ForfeoReserveBookingCommand {
  kind: ForfeoReserveBookingCommandKind;
  businessId: string;
  catalogItemId?: string;
  holdId?: string;
  bookingId?: string;
  startsAt?: string;
  endsAt?: string;
  partySize?: number;
  expectedRevision?: number;
  idempotencyKey: string;
  correlationId: string;
}

const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,199}$/;

export function isForfeoReserveBookingCommandKind(
  value: unknown,
): value is ForfeoReserveBookingCommandKind {
  return FORFEO_RESERVE_BOOKING_COMMANDS.includes(
    value as ForfeoReserveBookingCommandKind,
  );
}

export function isForfeoReserveBookingState(
  value: unknown,
): value is ForfeoReserveBookingState {
  return FORFEO_RESERVE_BOOKING_STATES.includes(
    value as ForfeoReserveBookingState,
  );
}

export function forfeoReserveBookingWindowsOverlap(
  first: ForfeoReserveInstantWindow,
  second: ForfeoReserveInstantWindow,
): boolean {
  if (
    !isForfeoReserveHalfOpenInterval(first.startsAt, first.endsAt)
    || !isForfeoReserveHalfOpenInterval(second.startsAt, second.endsAt)
  ) {
    return false;
  }
  return Date.parse(first.startsAt) < Date.parse(second.endsAt)
    && Date.parse(second.startsAt) < Date.parse(first.endsAt);
}

export function calculateForfeoReserveRemainingCapacity(
  totalCapacity: number,
  committedCapacity: number,
): number {
  if (!Number.isSafeInteger(totalCapacity) || totalCapacity < 0) return 0;
  if (!Number.isSafeInteger(committedCapacity) || committedCapacity < 0) {
    return 0;
  }
  return Math.max(0, totalCapacity - committedCapacity);
}

export function canForfeoReserveBookingTransition(
  from: ForfeoReserveBookingState,
  to: ForfeoReserveBookingState,
): boolean {
  const transitions: Record<ForfeoReserveBookingState, readonly ForfeoReserveBookingState[]> = {
    pending_confirmation: ['confirmed', 'cancelled', 'expired', 'manual_review'],
    pending_payment: ['confirmed', 'cancelled', 'expired', 'manual_review'],
    confirmed: ['completed', 'cancelled', 'no_show', 'manual_review'],
    completed: [],
    cancelled: [],
    expired: [],
    no_show: [],
    manual_review: ['confirmed', 'cancelled'],
  };
  return transitions[from].includes(to);
}

export function validateForfeoReserveAvailabilitySearch(
  query: ForfeoReserveAvailabilitySearch,
): readonly string[] {
  const errors: string[] = [];
  if (!query || typeof query !== 'object') return ['invalid_search'];
  if (!IDENTIFIER.test(String(query.businessId || ''))) {
    errors.push('invalid_business_id');
  }
  if (!IDENTIFIER.test(String(query.catalogItemId || ''))) {
    errors.push('invalid_catalog_item_id');
  }
  if (!isForfeoReserveHalfOpenInterval(query.startsAt, query.endsAt)) {
    errors.push('invalid_search_window');
  } else if (
    Date.parse(query.endsAt) - Date.parse(query.startsAt)
    > 31 * 24 * 60 * 60 * 1000
  ) {
    errors.push('search_window_too_large');
  }
  if (!Number.isSafeInteger(query.partySize) || query.partySize < 1 || query.partySize > 100000) {
    errors.push('invalid_party_size');
  }
  if (
    typeof query.timezone !== 'string'
    || query.timezone.length < 3
    || query.timezone.length > 100
  ) {
    errors.push('invalid_timezone');
  }
  if (
    typeof query.correlationId !== 'string'
    || query.correlationId.trim().length < 8
    || query.correlationId.length > 200
  ) {
    errors.push('invalid_correlation_id');
  }
  if (hasForfeoReserveForbiddenClientFields(query)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function validateForfeoReserveBookingCommand(
  command: ForfeoReserveBookingCommand,
): readonly string[] {
  const errors: string[] = [];
  if (!command || typeof command !== 'object') return ['invalid_command'];
  if (!isForfeoReserveBookingCommandKind(command.kind)) {
    errors.push('invalid_command_kind');
  }
  if (!IDENTIFIER.test(String(command.businessId || ''))) {
    errors.push('invalid_business_id');
  }
  if (!normalizeForfeoReserveIdempotencyKey(command.idempotencyKey)) {
    errors.push('invalid_idempotency_key');
  }
  if (
    typeof command.correlationId !== 'string'
    || command.correlationId.trim().length < 8
    || command.correlationId.length > 200
  ) {
    errors.push('invalid_correlation_id');
  }
  if (hasForfeoReserveForbiddenClientFields(command)) {
    errors.push('financial_fields_forbidden');
  }

  if (['create_hold', 'join_waitlist'].includes(command.kind)) {
    if (!IDENTIFIER.test(String(command.catalogItemId || ''))) {
      errors.push('catalog_item_id_required');
    }
    if (!isForfeoReserveHalfOpenInterval(command.startsAt, command.endsAt)) {
      errors.push('invalid_booking_window');
    }
    if (!Number.isSafeInteger(command.partySize) || Number(command.partySize) < 1) {
      errors.push('invalid_party_size');
    }
  }

  if (['release_hold', 'confirm_free'].includes(command.kind)) {
    if (!IDENTIFIER.test(String(command.holdId || ''))) {
      errors.push('hold_id_required');
    }
  }

  if (['cancel_booking', 'reschedule_booking', 'mark_no_show'].includes(command.kind)) {
    if (!IDENTIFIER.test(String(command.bookingId || ''))) {
      errors.push('booking_id_required');
    }
  }

  if (command.kind === 'reschedule_booking') {
    if (!isForfeoReserveHalfOpenInterval(command.startsAt, command.endsAt)) {
      errors.push('invalid_booking_window');
    }
  }

  if (
    ['release_hold', 'confirm_free', 'cancel_booking', 'reschedule_booking', 'mark_no_show']
      .includes(command.kind)
    && (
      !Number.isSafeInteger(command.expectedRevision)
      || Number(command.expectedRevision) < 1
    )
  ) {
    errors.push('expected_revision_required');
  }

  return errors;
}

export function assertForfeoReserveBookingBoundary(): true {
  if (
    FORFEO_RESERVE_AUTHORITIES.availability !== 'RESERVE'
    || FORFEO_RESERVE_AUTHORITIES.financial !== 'GIFTSHOP'
    || FORFEO_RESERVE_AUTHORITIES.discovery !== 'EXPLORE'
  ) {
    throw new Error('forfeo_reserve_authority_boundary_violation');
  }
  return true;
}
